import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { CApiRestful } from "../../api/CApiRestful";
import { IAppointment } from "../../interfaces/IAppointment";
import moment from "moment";
import enTranslation from "../../i18n/en/translation.json";
import msTranslation from "../../i18n/ms/translation.json";
import * as Constants from "../../constants";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ModApptDetail from "../mods/ModApptDetail";
import useMediaQuery from "@mui/material/useMediaQuery";
import ImgBooked from "../../assets/booked.png";
import { CDataUtil } from "../../utils/CDataUtil";

const TAG = "TabViewAppointment";

interface IProps {
  selectedLanguage: string;
  mobileNumber: string;
  jsonWsGetServiceListResp: any;
  jsonWsGetBranchListResp: any;
  signature: string;
}

const useStyles = makeStyles({
  root: {},
  accordion: {
    border: "1px solid #e5e5e5",
    borderRadius: "4px",
    "&:before": {
      display: "none",
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const StyledTab = styled(Tab)({
  "&.Mui-selected": {
    color: "#000000",
  },
});

const CustomizedAccordionSummary = styled(AccordionSummary)(
  ({ theme }) => `
    .MuiAccordionSummary-content {
      margin: 0;
    },
  `
);

const TabViewAppointment = (props: IProps) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isDesktop = useMediaQuery("(min-width:600px)");
  const isTablet = useMediaQuery("(min-width:300px)");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [expanded, setExpanded] = React.useState<string | false>("");
  const [isShowCancelApptDlg, setIsShowCancelApptDlg] = useState(false);
  const [isShowRescheduleApptDlg, setIsShowRescheduleApptDlg] = useState(false);
  const [jsonWsGetApptListResp, setJsonWsGetApptListResp] =
    useState<any>(undefined);
  const [customerName, setCustomerName] = useState<any>("");
  const [upcomingApptList, setUpcomingApptList] = useState<IAppointment[]>([]);
  const [previousApptList, setPreviousApptList] = useState<IAppointment[]>([]);
  const [selectedBranchCode, setSelectedBranchCode] = useState<
    string | undefined
  >(undefined);
  const [selectedAppt, setSelectedAppt] = useState<IAppointment | undefined>(
    undefined
  );
  const [selectedApptCode, setSelectedApptCode] = useState<string | undefined>(
    undefined
  );
  const [selectedServiceNo, setSelectedServiceNo] = useState<
    number | undefined
  >(undefined);
  const [selectedApptDate, setSelectedApptDate] = useState<Date | null>(null);
  const [selectedApptTime, setSelectedApptTime] = useState<string | undefined>(
    undefined
  );
  const [selectedAppointmentType, setSelectedAppointmentType] = useState("---");
  const [isShowAppointmentEditedDlg, setIsShowAppointmentEditedDlg] =
    useState(false);
  const [isShowAppointmentCancelledDlg, setIsShowAppointmentCancelledDlg] =
    useState(false);

  useEffect(() => {
    fetchApptList();
  }, []);

  useEffect(() => {
    if (jsonWsGetApptListResp) {
      refreshApptList();
    }
  }, [jsonWsGetApptListResp]);

  useEffect(() => {
    if (jsonWsGetApptListResp) {
      refreshApptList();
    }
  }, [props.selectedLanguage]);

  const fetchApptList = async () => {
    try {
      setIsLoading(true);
      const jsonWsGetApptListResp = await CApiRestful.getApptList(
        props.mobileNumber,
        props.mobileNumber,
        props.signature
      );
      setJsonWsGetApptListResp(jsonWsGetApptListResp);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCancelAppt = async () => {
    try {
      setIsLoading(true);

      if (!selectedBranchCode || selectedBranchCode.length == 0) {
        setError(t("viewAppt.invalidBranchCode"));
        return;
      }

      if (!selectedApptCode || selectedApptCode.length == 0) {
        setError(t("viewAppt.invalidApptCode"));
        return;
      }

      const jsonWsCancelApptResp = await CApiRestful.cancelAppt(
        selectedBranchCode,
        selectedApptCode,
        props.mobileNumber
      );

      if (jsonWsCancelApptResp.root) {
        if (jsonWsCancelApptResp.root.ErrorList) {
          if (jsonWsCancelApptResp.root.ErrorList.Error) {
            setError(jsonWsCancelApptResp.root.ErrorList.Error.Reason);
            return;
          } else {
            setError(t("viewAppt.errorCancelAppt"));
            return;
          }
        } else {
          setError(t("viewAppt.errorCancelAppt"));
          return;
        }
      } else if (jsonWsCancelApptResp.CancelApptResp) {
        if (jsonWsCancelApptResp.CancelApptResp.ErrorList) {
          if (jsonWsCancelApptResp.CancelApptResp.ErrorList.Error) {
            setError(
              jsonWsCancelApptResp.CancelApptResp.ErrorList.Error.Reason
            );
            return;
          } else {
            setError(t("viewAppt.errorCancelAppt"));
            return;
          }
        } else if (jsonWsCancelApptResp.CancelApptResp.Status != 0) {
          setError(t("viewAppt.errorCancelAppt"));
          return;
        }
      } else {
        setError(t("viewAppt.errorCancelAppt"));
        return;
      }

      setIsShowCancelApptDlg(false);
      setIsShowAppointmentCancelledDlg(true);
      setExpanded(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRescheduleAppt = async () => {
    var isSuccess = false;

    try {
      setIsLoading(true);

      if (!selectedApptCode || selectedApptCode.length == 0) {
        setError(t("viewAppt.invalidApptCode"));
        return;
      }

      if (selectedServiceNo == undefined) {
        setError(t("viewAppt.invalidServiceNo"));
        return;
      }

      if (!selectedApptDate) {
        setError(t("viewAppt.invalidApptDate"));
        return;
      }

      if (!selectedApptTime) {
        setError(t("viewAppt.invalidApptTime"));
        return;
      }

      if (!selectedBranchCode) {
        setError(t("viewAppt.invalidBranchCode"));
        return;
      }

      const jsonWsRescheduleApptResp = await CApiRestful.changeAppt(
        props.mobileNumber,
        props.mobileNumber,
        selectedApptCode,
        selectedServiceNo,
        selectedApptDate,
        selectedApptTime,
        selectedAppointmentType,
        selectedBranchCode,
        selectedAppt
      );

      if (jsonWsRescheduleApptResp.UpdateApptListResp) {
        if (jsonWsRescheduleApptResp.UpdateApptListResp.ErrorList) {
          if (jsonWsRescheduleApptResp.UpdateApptListResp.ErrorList.Error) {
            setError(
              jsonWsRescheduleApptResp.UpdateApptListResp.ErrorList.Error.Reason
            );
            return;
          } else {
            setError(t("viewAppt.errorRescheduleAppt"));
            return;
          }
        } else if (jsonWsRescheduleApptResp.UpdateApptListResp.Status != 0) {
          setError(t("viewAppt.errorRescheduleAppt"));
          return;
        }
      } else {
        setError(t("viewAppt.errorRescheduleAppt"));
        return;
      }

      setIsShowRescheduleApptDlg(false);
      setIsShowAppointmentEditedDlg(true);
      setExpanded(false);

      isSuccess = true;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }

    return isSuccess;
  };

  const refreshApptList = () => {
    try {
      const _upcomingApptList: IAppointment[] = [];
      const _previousApptList: IAppointment[] = [];

      if (!jsonWsGetApptListResp || !jsonWsGetApptListResp.ApptList) {
        return;
      }

      if (jsonWsGetApptListResp.ApptList.length > 0) {
        if (jsonWsGetApptListResp.ApptList) {
          for (let _appt of jsonWsGetApptListResp.ApptList) {
            const objAppt = buildAppointmentObject(_appt);
            if (objAppt) {
              const apptDateTime = mergeApptDateTime(
                objAppt.apptDate,
                objAppt.apptTime
              );
              if (moment(apptDateTime).isBefore(moment())) {
                // previous appointment
                _previousApptList.push(objAppt);
              } else {
                // upcoming appointment
                _upcomingApptList.push(objAppt);
              }
            }
          }
        }
      } else {
        if (jsonWsGetApptListResp.ApptList.Appt) {
          const objAppt = buildAppointmentObject(
            jsonWsGetApptListResp.ApptList.Appt
          );
          if (objAppt) {
            const apptDateTime = mergeApptDateTime(
              objAppt.apptDate,
              objAppt.apptTime
            );
            if (moment(apptDateTime).isBefore(moment())) {
              // previous appointment
              _previousApptList.push(objAppt);
            } else {
              // upcoming appointment
              _upcomingApptList.push(objAppt);
            }
          }
        }
      }

      setUpcomingApptList(_upcomingApptList);
      setPreviousApptList(_previousApptList);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const buildAppointmentObject = (_appt: any) => {
    var objAppt: IAppointment | undefined;

    try {
      const apptDate = _appt.ApptDate;
      const apptTime = _appt.ApptTime;
      const apptStatus = _appt.ApptStatus;
      const apptSlotId = _appt.ApptSlot.IndApptSlot.ApptSlotId;
      const custId = _appt.Cust.IndCust.CustId;

      var emailAddr = _appt.CustInfo?.EmailAddr ?? "";
      if (emailAddr.length == 0) {
        emailAddr = _appt.Cust.IndCust.EmailAddr;
      }

      var name = _appt.CustInfo?.Name ?? "";
      if (name.length == 0) {
        name = _appt.Cust.IndCust.Name;
      }

      const mobilePhoneNumber = _appt.Cust.MobilePhoneNumber;
      const appointmentType = _appt.CustInfo?.AppointmentType ?? "";
      const apptCode = _appt.IndAppt.ApptCode;
      const apptId = _appt.IndAppt.ApptId;
      const branchCode = _appt.IndBranch.BranchCode;

      var branchName = _appt.IndBranch.BranchName;
      const languageStringList =
        _appt.Branch.LanguageStringList;
      const displayStringValue =
        CDataUtil.extractDisplayStringValueByLanguageId(
          languageStringList,
          props.selectedLanguage
        );
      if (displayStringValue.length > 0) {
        branchName = displayStringValue;
      }

      const branchLongitude = _appt.Branch.Longitude;
      const branchLatitude = _appt.Branch.Latitude;
      const serviceNo = _appt.Service.IndService.ServiceNo;

      var serviceName = _appt.Service.IndService.ServiceName;
      const languageStringList2 = _appt.Service.LanguageStringList;
      const displayStringValue2 =
        CDataUtil.extractDisplayStringValueByLanguageId(
          languageStringList2,
          props.selectedLanguage
        );
      if (displayStringValue2.length > 0) {
        serviceName = displayStringValue2;
      }

      const zoomLink = _appt.ZoomLink;
      const zoomPassword = _appt.ZoomPassword;

      var policyCertificateNum = _appt.CustInfo?.PolicyNumber ?? "";
      if (policyCertificateNum.length == 0) {
        policyCertificateNum = _appt.Cust.IndCust.OtherId;
      }

      var remarks = _appt.CustInfo?.Remarks ?? "";
      if (remarks.length == 0) {
        remarks = _appt.Cust.IndCust.Name2;
      }

      var serviceGrpName = undefined;
      if (_appt.Service.ServiceGrpList) {
        if (_appt.Service.ServiceGrpList.length > 0) {
          serviceGrpName =
            _appt.Service.ServiceGrpList[0].IndServiceGrp.ServiceGrpName;
        } else {
          serviceGrpName =
            _appt.Service.ServiceGrpList.ServiceGrp.IndServiceGrp
              .ServiceGrpName;
        }
      }

      setCustomerName(name);

      objAppt = {
        apptDate: moment(apptDate).toDate(),
        apptTime: moment(apptTime).toDate(),
        apptStatus: apptStatus,
        apptSlotId: apptSlotId,
        custId: custId,
        emailAddr: emailAddr,
        name: name,
        mobilePhoneNumber: mobilePhoneNumber,
        appointmentType: appointmentType,
        apptCode: apptCode,
        apptId: apptId,
        branchCode: branchCode,
        branchName: branchName,
        branchLongitude: branchLongitude,
        branchLatitude: branchLatitude,
        serviceNo: serviceNo,
        serviceName: serviceName,
        serviceGrpName: serviceGrpName,
        zoomLink: zoomLink,
        zoomPassword: zoomPassword,
        policyCertificateNum: policyCertificateNum,
        remarks: remarks,
      };
    } catch (err: any) {
      setError(err.message);
    }

    return objAppt;
  };

  const mergeApptDateTime = (apptDate: Date, apptTime: Date) => {
    var apptDateTime;

    try {
      const apptDateStr = moment(apptDate).format("YYYY-MM-DD");
      const apptTimeStr = moment(apptTime).format("HH:mm:ss");
      apptDateTime = moment(apptDateStr + "T" + apptTimeStr).toDate();
    } catch (err: any) {
      setError(err.message);
    }

    return apptDateTime;
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const showAccordion = (appt: IAppointment, index: number) => {
    const apptDateStr = moment(appt.apptDate).format("YYYY-MM-DD");
    const apptTimeStr = moment(appt.apptTime).format("HH:mm:ss");
    const mmEditApptCutOffTime = moment(apptDateStr + "T" + apptTimeStr).add(
      Constants.MIN_ALLOW_APPOINTMENT_PERIOD_MIN * -1,
      "minutes"
    );

    const autoDetectAppointmentTypeLanguage = (appointmentType: string) => {
      var finalAppointmentType = appointmentType;

      try {
        if (
          appointmentType == enTranslation.makeAppt.apptTypeVirtual ||
          appointmentType == msTranslation.makeAppt.apptTypeVirtual
        ) {
          // Virtual
          finalAppointmentType = t("makeAppt.apptTypeVirtual");
        } else {
          // Branch
          finalAppointmentType = t("makeAppt.apptTypeBranch");
        }
      } catch (err: any) {}

      return finalAppointmentType;
    };

    const autoDetectServiceGrpNameLanguage = (serviceGrpName: string) => {
      var finalServiceGrpName = serviceGrpName;

      try {
        if (serviceGrpName == enTranslation.makeAppt.serviceGrpGeneral) {
          // General
          finalServiceGrpName = t("makeAppt.serviceGrpGeneral");
        } else if (
          serviceGrpName == enTranslation.makeAppt.serviceGrpLifeFamily
        ) {
          // Life & Family
          finalServiceGrpName = t("makeAppt.serviceGrpLifeFamily");
        } else if (
          serviceGrpName == enTranslation.makeAppt.serviceGrpGroupPolicy
        ) {
          // Group Policy
          finalServiceGrpName = t("makeAppt.serviceGrpGroupPolicy");
        }
      } catch (err: any) {}

      return finalServiceGrpName;
    };

    return (
      <Accordion
        expanded={expanded === "panel" + index.toString()}
        onChange={handleChange("panel" + index.toString())}
        className={classes.accordion}
        sx={{ marginBottom: "16px" }}
        elevation={0}
        square={true}
      >
        <CustomizedAccordionSummary
          sx={{
            "&.Mui-expanded": {
              minHeight: 0,
            },
          }}
          expandIcon={<ExpandMoreIcon htmlColor="#000000" />}
          id={"panel-header" + index.toString()}
        >
          {expanded !== "panel" + index.toString() && (
            <Box display="flex">
              <Box
                pt={2}
                pb={2}
                pr={isDesktop ? 2 : isTablet ? 1 : 0}
                flexDirection={"column"}
                width={isDesktop ? "160px" : "120px"}
                textAlign={"center"}
                borderRight={1}
                borderColor={"#e5e5e5"}
              >
                <Box fontSize="28px" fontWeight={"bold"}>
                  {moment(appt.apptDate).format("DD MMM")}
                </Box>
                <Box fontSize="16px" fontWeight={"bold"}>
                  {moment(appt.apptTime).format("h:mm A")}
                </Box>
              </Box>
              <Grid container>
                <Box
                  pt={2}
                  pb={2}
                  pl={isDesktop ? 3 : isTablet ? 2 : 1}
                  pr={isDesktop ? 3 : isTablet ? 2 : 1}
                  flexDirection={"column"}
                  minWidth={isDesktop ? "120px" : "100px"}
                  maxWidth={isDesktop ? "300px" : isTablet ? "200px" : "100px"}
                >
                  <Box fontSize="12px">{t("viewAppt.appointmentType")}</Box>
                  <Box fontSize="16px" fontWeight={"bold"}>
                    {autoDetectAppointmentTypeLanguage(appt.appointmentType)}
                  </Box>
                </Box>
                {appt.appointmentType !=
                  enTranslation.makeAppt.apptTypeVirtual &&
                  appt.appointmentType !=
                    msTranslation.makeAppt.apptTypeVirtual && (
                    <Box
                      pt={2}
                      pb={2}
                      pl={isDesktop ? 3 : isTablet ? 2 : 1}
                      pr={isDesktop ? 3 : isTablet ? 2 : 1}
                      flexDirection={"column"}
                      minWidth={isDesktop ? "120px" : "100px"}
                      maxWidth={
                        isDesktop ? "200px" : isTablet ? "200px" : "100px"
                      }
                    >
                      <Box fontSize="12px">{t("viewAppt.branchLocation")}</Box>
                      <Box fontSize="16px" fontWeight={"bold"}>
                        {appt.branchName}
                      </Box>
                    </Box>
                  )}

                <Box
                  pt={2}
                  pb={2}
                  pl={isDesktop ? 3 : isTablet ? 2 : 1}
                  pr={isDesktop ? 3 : isTablet ? 2 : 1}
                  flexDirection={"column"}
                  minWidth={isDesktop ? "120px" : "100px"}
                  maxWidth={isDesktop ? "200px" : isTablet ? "200px" : "100px"}
                >
                  <Box fontSize="12px">
                    {t("viewAppt.purposeOfAppointment")}
                  </Box>
                  <Box fontSize="16px" fontWeight={"bold"}>
                    {appt.serviceName}
                  </Box>
                </Box>
              </Grid>
            </Box>
          )}
        </CustomizedAccordionSummary>
        <AccordionDetails>
          <Box
            pl={isDesktop ? 3 : isTablet ? 1 : 0}
            pr={isDesktop ? 2 : isTablet ? 1 : 0}
            pb={2}
          >
            <Box fontSize={"18px"} fontWeight="bold">
              {t("viewAppt.appointmentDetails")}
            </Box>
            <Box pt={2} fontSize={"16px"}>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.appointmentType")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {autoDetectAppointmentTypeLanguage(appt.appointmentType)}
                </Box>
              </Box>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.purposeOfAppointment")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {appt.serviceName}
                </Box>
              </Box>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.date")}
                </Box>
                <Box fontWeight={"bold"} pl={1} sx={{ whiteSpace: "nowrap" }}>
                  {moment(appt.apptDate).format("DD MMM YYYY")}
                </Box>
              </Box>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.time")}
                </Box>
                <Box fontWeight={"bold"} pl={1} sx={{ whiteSpace: "nowrap" }}>
                  {moment(appt.apptTime).format("h:mm A")}
                </Box>
              </Box>

              {appt.appointmentType == enTranslation.makeAppt.apptTypeVirtual ||
              appt.appointmentType ===
                msTranslation.makeAppt.apptTypeVirtual ? (
                <Box>
                  <Box pt={1} display="flex">
                    <Box
                      color={"#767676"}
                      minWidth={
                        isDesktop ? "220px" : isTablet ? "160px" : "100px"
                      }
                      maxWidth={
                        isDesktop ? "220px" : isTablet ? "160px" : "100px"
                      }
                    >
                      {t("viewAppt.zoomLink")}
                    </Box>
                    <Box
                      display="flex"
                      sx={{ wordWrap: "break-word", minWidth: "10px" }}
                    >
                      <Box
                        fontWeight={"bold"}
                        pl={1}
                        sx={{ wordWrap: "break-word", minWidth: "10px" }}
                      >
                        {appt.zoomLink ?? t("viewAppt.notAvailable")}
                      </Box>
                      {appt.zoomLink && appt.zoomLink.length > 0 && (
                        <Box pl={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(appt.zoomLink!);
                            }}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box pt={1} display="flex">
                    <Box
                      color={"#767676"}
                      minWidth={
                        isDesktop ? "220px" : isTablet ? "160px" : "100px"
                      }
                      maxWidth={
                        isDesktop ? "220px" : isTablet ? "160px" : "100px"
                      }
                    >
                      {t("viewAppt.zoomPassword")}
                    </Box>
                    <Box
                      display="flex"
                      sx={{ wordWrap: "break-word", minWidth: "10px" }}
                    >
                      <Box
                        fontWeight={"bold"}
                        pl={1}
                        sx={{ wordWrap: "break-word", minWidth: "10px" }}
                      >
                        {appt.zoomPassword ?? t("viewAppt.notAvailable")}
                      </Box>
                      {appt.zoomPassword && appt.zoomPassword.length > 0 && (
                        <Box pl={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(appt.zoomPassword!);
                            }}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box pt={1} display="flex">
                  <Box
                    color={"#767676"}
                    minWidth={
                      isDesktop ? "220px" : isTablet ? "160px" : "100px"
                    }
                    maxWidth={
                      isDesktop ? "220px" : isTablet ? "160px" : "100px"
                    }
                  >
                    {t("viewAppt.branchLocation")}
                  </Box>
                  <Box
                    display="flex"
                    sx={{ wordWrap: "break-word", minWidth: "10px" }}
                  >
                    <Box
                      fontWeight={"bold"}
                      pl={1}
                      sx={{ wordWrap: "break-word", minWidth: "10px" }}
                    >
                      {appt.branchName}
                    </Box>
                    {appt.branchLongitude && appt.branchLatitude && (
                      <Box pl={1}>
                        <IconButton
                          onClick={() => {
                            window.open(
                              "https://maps.google.com/?q=" +
                                appt.branchLatitude +
                                "," +
                                appt.branchLongitude
                            );
                          }}
                        >
                          <LocationOnIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Box pt={5} fontSize={"18px"} fontWeight="bold">
              {t("viewAppt.contactDetails")}
            </Box>
            <Box pt={2} fontSize={"16px"}>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.name")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {appt.name}
                </Box>
              </Box>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.email")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {appt.emailAddr}
                </Box>
              </Box>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.phoneNum")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {appt.mobilePhoneNumber}
                </Box>
              </Box>
            </Box>

            <Box pt={5} fontSize={"18px"} fontWeight="bold">
              {t("viewAppt.policyDetails")}
            </Box>
            <Box pt={2} fontSize={"16px"}>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.policyNum")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {appt.policyCertificateNum ?? t("viewAppt.notAvailable")}
                </Box>
              </Box>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.typeOfInsuranceTakaful")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {autoDetectServiceGrpNameLanguage(appt.serviceGrpName)}
                </Box>
              </Box>
              <Box pt={1} display="flex">
                <Box
                  color={"#767676"}
                  maxWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                  minWidth={isDesktop ? "220px" : isTablet ? "160px" : "100px"}
                >
                  {t("viewAppt.remarks")}
                </Box>
                <Box
                  fontWeight={"bold"}
                  pl={1}
                  sx={{ wordWrap: "break-word", minWidth: "10px" }}
                >
                  {appt.remarks ?? t("viewAppt.notAvailable")}
                </Box>
              </Box>
            </Box>

            {moment().isBefore(mmEditApptCutOffTime) ? (
              <Box textAlign={"right"} pt={2}>
                <Button
                  sx={{
                    width: "180px",
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: "40px",
                    bgcolor: "transparent",
                    borderRadius: "4px",
                    border: "1px solid #707070",
                    margin: "8px",
                  }}
                  disableElevation
                  variant="contained"
                  onClick={() => {
                    setSelectedAppt(appt);
                    setSelectedApptCode(appt.apptCode);
                    setSelectedApptDate(appt.apptDate);
                    setSelectedServiceNo(appt.serviceNo);
                    setSelectedBranchCode(appt.branchCode);
                    setSelectedApptTime(undefined);
                    setIsShowRescheduleApptDlg(true);
                  }}
                >
                  {t("viewAppt.reschedule")}
                </Button>

                <Button
                  sx={{
                    width: "180px",
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: "40px",
                    borderRadius: "4px",
                    bgcolor: "#e75300",
                    color: "#FFFFFF",
                    margin: "8px",
                    lineHeight: "18px",
                  }}
                  disableElevation
                  variant="contained"
                  onClick={() => {
                    setSelectedAppt(appt);
                    setSelectedBranchCode(appt.branchCode);
                    setSelectedApptCode(appt.apptCode);
                    setIsShowCancelApptDlg(true);
                  }}
                >
                  {t("viewAppt.cancelAppointment")}
                </Button>
              </Box>
            ) : (
              (appt.appointmentType == enTranslation.makeAppt.apptTypeVirtual ||
                appt.appointmentType ===
                  msTranslation.makeAppt.apptTypeVirtual) &&
              appt.zoomLink &&
              appt.zoomLink.length > 0 && (
                <Box textAlign={"right"} pt={2}>
                  <Button
                    sx={{
                      width: "180px",
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "16px",
                      height: "40px",
                      borderRadius: "4px",
                      margin: "8px",
                    }}
                    disableElevation
                    variant="contained"
                    onClick={() => {
                      window.open(appt.zoomLink);
                    }}
                  >
                    {t("viewAppt.joinAppointment")}
                  </Button>
                </Box>
              )
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      <Box fontSize="20px" fontWeight={"bold"} pl={1} pr={1}>
        {t("viewAppt.greeting1") + customerName + t("viewAppt.greeting2")}
      </Box>

      <Box pt={2} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedTab}
          TabIndicatorProps={{
            sx: {
              height: "4px",
            },
          }}
          onChange={(e, value) => {
            setSelectedTab(value);
          }}
          aria-label="basic tabs"
        >
          <StyledTab
            label={t("viewAppt.upcomingAppointment")}
            sx={{
              fontWeight: "bold",
              fontSize: "16px",
              textTransform: "none",
              width: isDesktop ? "200px" : "130px",
            }}
            wrapped
          />
          <StyledTab
            label={t("viewAppt.previousAppointment")}
            sx={{
              fontWeight: "bold",
              fontSize: "16px",
              textTransform: "none",
              width: isDesktop ? "200px" : "130px",
            }}
            wrapped
          />
        </Tabs>
      </Box>

      <TabPanel value={selectedTab} index={0}>
        {upcomingApptList.length > 0 ? (
          upcomingApptList.map((appt, index) => showAccordion(appt, index))
        ) : (
          <Box fontWeight={"bold"} textAlign={"center"}>
            {t("viewAppt.appointmentNotFound")}
          </Box>
        )}
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
        {previousApptList.length > 0 ? (
          previousApptList.map((appt, index) => showAccordion(appt, index))
        ) : (
          <Box fontWeight={"bold"} textAlign={"center"}>
            {t("viewAppt.appointmentNotFound")}
          </Box>
        )}
      </TabPanel>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          open={error.length > 0}
          onClose={() => setError("")}
        >
          <Alert
            onClose={() => setError("")}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Stack>

      <Dialog
        PaperProps={{
          style: { borderRadius: "12px" },
        }}
        open={isShowCancelApptDlg}
        keepMounted
      >
        <DialogTitle></DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box
              fontSize={"24px"}
              fontWeight="bold"
              color={"#000000"}
              textAlign="center"
            >
              {t("viewAppt.cancelAppointmentTitle")}
            </Box>
            <Box color={"#000000"} textAlign="center">
              {t("viewAppt.cancelAppointmentDesc")}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Grid pb={2} container alignItems="center" justifyContent={"center"}>
            <Button
              sx={{
                width: "180px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "16px",
                height: "40px",
                bgcolor: "transparent",
              }}
              disableElevation
              variant="contained"
              onClick={() => setIsShowCancelApptDlg(false)}
            >
              {t("viewAppt.cancel")}
            </Button>
            <Button
              sx={{
                width: "180px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "16px",
                height: "40px",
                borderRadius: "4px",
                bgcolor: "#e75300",
                color: "#FFFFFF",
                lineHeight: "18px",
              }}
              disableElevation
              variant="contained"
              onClick={async () => {
                await fetchCancelAppt();
              }}
            >
              {t("viewAppt.cancelAppointment")}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog
        PaperProps={{
          style: { borderRadius: "12px", width: "598px" },
        }}
        open={isShowRescheduleApptDlg}
        sx={{
          "& .MuiDialogContent-root": {
            padding: 1,
          },
          "& .MuiPaper-root": {
            margin: 1,
          },
        }}
      >
        <DialogTitle>
          <Box
            pl={3}
            pr={3}
            pt={3}
            fontSize={"24px"}
            fontWeight="bold"
            color={"#333333"}
          >
            {t("viewAppt.rescheduleAppointment")}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            pl={isDesktop ? 3 : isTablet ? 1 : 0}
            pr={isDesktop ? 3 : isTablet ? 1 : 0}
          >
            <Box>
              {selectedServiceNo && selectedApptDate && (
                <ModApptDetail
                  jsonWsGetServiceListResp={props.jsonWsGetServiceListResp}
                  jsonWsGetBranchListResp={props.jsonWsGetBranchListResp}
                  selectedLanguage={props.selectedLanguage}
                  selectedServiceNo={selectedServiceNo}
                  selectedBranchCode={selectedBranchCode}
                  selectedApptDate={selectedApptDate}
                  onError={(err: any) => {
                    setError(err.message);
                  }}
                  onChange={(
                    branchCode?: string,
                    apptDate?: Date,
                    apptTime?: string,
                    appointmentType?: string
                  ) => {
                    if (branchCode && branchCode.length > 0) {
                      setSelectedBranchCode(branchCode);
                    }

                    if (apptDate) {
                      setSelectedApptDate(apptDate);
                      setSelectedApptTime(undefined);
                    }

                    if (apptTime && apptTime.length > 0) {
                      setSelectedApptTime(apptTime);
                    }

                    if (appointmentType && appointmentType.length > 0) {
                      setSelectedAppointmentType(appointmentType);
                    }
                  }}
                />
              )}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Grid pb={2} container alignItems="center" justifyContent={"center"}>
            <Button
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "16px",
                height: "56px",
                borderRadius: "5px",
                lineHeight: "18px",
              }}
              disabled={
                selectedBranchCode &&
                selectedApptCode &&
                selectedServiceNo &&
                selectedApptDate &&
                selectedApptTime
                  ? false
                  : true
              }
              fullWidth
              variant="contained"
              onClick={async () => {
                const isSuccess = await fetchRescheduleAppt();
              }}
            >
              {t("viewAppt.confirmRescheduleAppointment")}
            </Button>
            <Button
              disableElevation
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                height: "56px",
                backgroundColor: "transparent",
                marginTop: "16px",
                color: "#04AAFF",
              }}
              fullWidth
              variant="contained"
              onClick={() => setIsShowRescheduleApptDlg(false)}
            >
              {t("viewAppt.cancel")}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog open={isShowAppointmentEditedDlg} keepMounted>
        <DialogTitle></DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box textAlign="center">
              <img src={ImgBooked} height={131} />
            </Box>
            <Box
              fontSize={"20px"}
              fontWeight={"bold"}
              color={"#000000"}
              pt={2}
              textAlign="center"
            >
              {t("viewAppt.thankYou")}
            </Box>
            <Box fontSize={"16px"} color={"#000000"} textAlign="center">
              <Box>{t("viewAppt.appointmentEditedDesc1")}</Box>
              <Box>{t("viewAppt.appointmentEditedDesc2")}</Box>
              <Box>{t("viewAppt.appointmentGenericDesc3")}</Box>
              <Box>{t("viewAppt.appointmentGenericDesc4")}</Box>
            </Box>
            <Box pt={5}>
              <Button
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "16px",
                  height: "56px",
                  backgroundColor: "transparent",
                  marginTop: "16px",
                  color: "#04AAFF",
                }}
                fullWidth
                variant="contained"
                onClick={() => {
                  setIsShowAppointmentEditedDlg(false);
                  fetchApptList();
                }}
              >
                {t("viewAppt.backToHome")}
              </Button>
            </Box>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Dialog open={isShowAppointmentCancelledDlg} keepMounted>
        <DialogTitle></DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box textAlign="center">
              <img src={ImgBooked} height={131} />
            </Box>
            <Box
              fontSize={"20px"}
              fontWeight={"bold"}
              color={"#000000"}
              pt={2}
              textAlign="center"
            >
              {t("viewAppt.thankYou")}
            </Box>
            <Box fontSize={"16px"} color={"#000000"} textAlign="center">
              <Box>{t("viewAppt.appointmentCancelledDesc1")}</Box>
              <Box>{t("viewAppt.appointmentCancelledDesc2")}</Box>
              <Box>{t("viewAppt.appointmentGenericDesc3")}</Box>
              <Box>{t("viewAppt.appointmentGenericDesc4")}</Box>
            </Box>
            <Box pt={5}>
              <Button
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "16px",
                  height: "56px",
                  backgroundColor: "transparent",
                  marginTop: "16px",
                  color: "#04AAFF",
                }}
                fullWidth
                variant="contained"
                onClick={() => {
                  setIsShowAppointmentCancelledDlg(false);
                  fetchApptList();
                }}
              >
                {t("viewAppt.backToHome")}
              </Button>
            </Box>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TabViewAppointment;
